/**
 * How an amount is written, in one place (N23).
 *
 * The twin of `backend/services/money.py`, and kept deliberately dull so the
 * two cannot disagree: group by thousands with a comma, always two decimals,
 * dollar sign in front. Before this, nine React files called `toFixed(2)` and
 * the email templates used `f"${v:.2f}"`; they agreed on the cents and on
 * nothing else, so a floor at 1,150 square feet reached the FixyBee as
 * `$17250.00`.
 *
 * Deliberately NOT `Intl.NumberFormat(i18n.language)`. The marketplace is
 * US-only and the Spanish interface still shows US dollars written the US way:
 * a Spanish reader seeing "17.250,00" would have to work out which convention
 * this app is using, on the screen where the amount is the whole point.
 */
const GROUPED = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PLAIN = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });

/** `17250` -> `$17,250.00`. A value that is not a number returns `fallback`. */
export function money(value, fallback = "") {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || typeof n !== "number" || !Number.isFinite(n)) {
    // A blank, never `$0.00`: a line whose price is missing must not render as
    // a line that costs nothing.
    return fallback;
  }
  return `$${GROUPED.format(n)}`;
}

/** `1150` -> `1,150`, `1.5` -> `1.5`. Quantities keep only the decimals they have. */
export function quantity(value, fallback = "") {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || typeof n !== "number" || !Number.isFinite(n)) {
    return fallback;
  }
  return PLAIN.format(n);
}

export default money;

/**
 * Tax on a subtotal at a percentage rate, or null when there is no rate (N34).
 *
 * The twin of `services.dany.tools.quote_tax` and of the arithmetic
 * `routes.quotes.send_quote` stores with, and the three have to agree to the
 * cent — a card that previews a total a dollar short of the one the client
 * receives is a support ticket.
 *
 * `Math.round(subtotal * rate) / 100` and not `round(subtotal * rate / 100)`:
 * multiplying first keeps the value an integer number of cents for the rates
 * people actually type, so the rounding decides on an exact number rather than
 * on whatever the extra division left behind. Daniel's case — 1,150 sq ft at
 * $15, 8.25% — is exactly $1,423.125, and this gives $1,423.13, which is what
 * the server's `ROUND_HALF_UP` gives and what a person gets doing it by hand.
 *
 * `null` in, `null` out. A FixyBee who has never named a rate has no tax, and
 * that is not the same as a tax of zero (T4b).
 */
export function taxOn(rate, subtotal) {
  const r = typeof rate === "string" ? Number(rate) : rate;
  if (r === null || r === undefined || !Number.isFinite(r)) return null;
  const base = typeof subtotal === "string" ? Number(subtotal) : subtotal;
  if (!Number.isFinite(base)) return null;
  return Math.round(base * r) / 100;
}
